import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaAnalyticsGateway1Service } from '../service';
import { GetScheduleDataIO } from '../types';

export const getGetScheduleDataQueryKey = (req: GetScheduleDataIO['input']) =>
  ['AnalyticsGatewayV1', 'GetScheduleData', req] as const;
type QueryKey = ReturnType<typeof getGetScheduleDataQueryKey>;

/**
 * A hook that returns a query for the `GetScheduleData` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetScheduleDataQuery = <E = unknown, D = GetScheduleDataIO['output']>(
  req: GetScheduleDataIO['input'],
  options?: LimitedSchemaQueryOptions<GetScheduleDataIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<GetScheduleDataIO['output'], E, D, QueryKey>({
    queryKey: getGetScheduleDataQueryKey(req),
    queryFn: () => SchemaAnalyticsGateway1Service.GetScheduleData(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `GetScheduleData` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetScheduleDataQueries = <E = unknown, D = GetScheduleDataIO['output']>(
  argsArr: {
    req: GetScheduleDataIO['input'];
    options?: LimitedSchemaQueryOptions<GetScheduleDataIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getGetScheduleDataQueryKey(req),
      queryFn: () => SchemaAnalyticsGateway1Service.GetScheduleData(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useGetScheduleDataQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `GetScheduleData` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchGetScheduleData = () => {
  const queryClient = useQueryClient();

  const fetchGetScheduleData = async <E = unknown, D = GetScheduleDataIO['output']>(
    req: GetScheduleDataIO['input'],
    options?: LimitedSchemaFetchQueryOptions<GetScheduleDataIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<GetScheduleDataIO['output'], E, D, QueryKey>(
      getGetScheduleDataQueryKey(req),
      () => SchemaAnalyticsGateway1Service.GetScheduleData(req, httpOptions),
      options
    );

    return response;
  };

  return fetchGetScheduleData;
};
